import type { Category } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import {
	legacyToResolvedMenu,
	type ResolveMenuOptions
} from "@nwt/norce-pwa/dist/runtime/utils/menu/resolveMenu";
import type { MenuLink, ResolvedMenuNode } from "~/types/storyblok/storyblok";

/** "/https://…", "/mailto:…", "/tel:…" — a link, not a site path. */
const PROTOCOL_HREF = /^\/?(https?:\/\/|mailto:|tel:)/i;

/**
 * Nodes an editor left without a name would render as empty links: a nameless
 * leaf is dropped, a nameless group is replaced by its children so its
 * subtree stays reachable.
 */
const withNames = (nodes: ResolvedMenuNode[]): ResolvedMenuNode[] =>
	nodes.flatMap((node) => {
		const children = withNames(node.children);
		if (node.name?.trim()) return [{ ...node, children }];
		return children;
	});

/**
 * legacyToResolvedMenu types a Storyblok URL-type link as "story" with href
 * "/https://…" (its cached_url is the URL itself) — likewise mailto: and tel:;
 * make it a real external link.
 */
const fixLegacyExternal = (node: ResolvedMenuNode): ResolvedMenuNode =>
	node.type === "story" && PROTOCOL_HREF.test(node.href)
		? { ...node, type: "external", href: node.href.replace(/^\//, "") }
		: node;

/**
 * The header menu: the Storyblok `MenuNode` menu when editors have built one,
 * otherwise the legacy Norce categories + Storyblok links in the same shape.
 */
export const pickEffectiveMenu = (
	menu: ResolvedMenuNode[] | undefined,
	legacyCategories: Category[] | undefined,
	legacyLinks: MenuLink[] | undefined,
	options: ResolveMenuOptions
): ResolvedMenuNode[] => {
	if (menu?.length) return withNames(menu);
	return withNames(
		legacyToResolvedMenu(legacyCategories, legacyLinks, options).map(
			fixLegacyExternal
		)
	);
};

const segments = (path: string): string[] => path.split("/").filter(Boolean);

/** True when `path` is `href` or below it, compared from the first segment. */
export const isPathUnder = (path: string, href: string): boolean => {
	const target = segments(href);
	const current = segments(path);
	return (
		target.length > 0 &&
		target.length <= current.length &&
		target.every((segment, i) => current[i] === segment)
	);
};

/**
 * A category node is active on its own page and anywhere below it, or when
 * one of its children is. `hrefFor` gives the localized href (with the
 * locale prefix), so it lines up with the route path.
 */
export const isMenuNodeActive = (
	node: ResolvedMenuNode,
	path: string,
	hrefFor: (node: ResolvedMenuNode) => string
): boolean =>
	(node.type === "category" && isPathUnder(path, hrefFor(node))) ||
	node.children.some((child) => isMenuNodeActive(child, path, hrefFor));
